import { useAuthStore } from '../stores/authStore'
import { useSyncStore } from '../stores/syncStore'

const FOLDER_NAME = 'PraxisCRM_Data'
const SETTINGS_FOLDER = 'settings'
const DRIVE_API = 'https://www.googleapis.com/drive/v3'
const UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3'

class GoogleDriveService {
  private writeQueue: Promise<void> = Promise.resolve()
  private queueCount = 0

  private getAccessToken(): string {
    const token = useAuthStore.getState().accessToken
    if (!token) throw new Error('Not authenticated')
    return token
  }

  private updateQueueStatus() {
    useSyncStore.getState().setQueueLength(this.queueCount)
  }

  private async request<T>(
    url: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const token = this.getAccessToken()
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    })

    // Handle token expiration - attempt refresh and retry once
    if (response.status === 401 && !isRetry) {
      console.log('[ SYSTEM: TOKEN EXPIRED. ATTEMPTING REFRESH... ]')
      const refreshed = await useAuthStore.getState().refreshToken()
      if (refreshed) {
        return this.request<T>(url, options, true)
      }
      throw new Error('Session expired. Please sign in again.')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error?.message || `Request failed: ${response.status}`)
    }

    if (response.status === 204) return {} as T
    return response.json()
  }

  async initHandshake(): Promise<boolean> {
    try {
      console.log('[ SYSTEM: INITIATING HANDSHAKE WITH GOOGLE DRIVE... ]')
      
      let folderId = await this.findFolder()
      
      if (!folderId) {
        console.log('[ SYSTEM: CREATING DATA FOLDER... ]')
        folderId = await this.createFolder()
      }

      useSyncStore.getState().setFolderId(folderId)
      useSyncStore.getState().setConnected(true)

      await this.writeHeartbeat()
      
      console.log('[ SYSTEM: HANDSHAKE COMPLETE. YOUR DATA AWAITS. ]')
      return true
    } catch (error) {
      console.error('[ SYSTEM: HANDSHAKE FAILED ]', error)
      useSyncStore.getState().setConnected(false)
      return false
    }
  }

  private async findFolder(): Promise<string | null> {
    const query = encodeURIComponent(
      `name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`
    )
    
    const result = await this.request<{ files: Array<{ id: string }> }>(
      `${DRIVE_API}/files?q=${query}&spaces=drive`
    )

    return result.files[0]?.id ?? null
  }

  private async createFolder(): Promise<string> {
    const metadata = {
      name: FOLDER_NAME,
      mimeType: 'application/vnd.google-apps.folder',
    }

    const result = await this.request<{ id: string }>(
      `${DRIVE_API}/files`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metadata),
      }
    )

    return result.id
  }

  private async writeHeartbeat(): Promise<void> {
    const heartbeat = {
      lastAccess: new Date().toISOString(),
      version: '1.0.0',
      client: 'PraxisCRM',
    }
    await this.writeEntity('heartbeat.json', heartbeat)
  }

  async readEntity<T>(fileName: string): Promise<T | null> {
    try {
      const folderId = useSyncStore.getState().folderId
      if (!folderId) throw new Error('Folder not initialised')

      const query = encodeURIComponent(
        `name='${fileName}' and '${folderId}' in parents and trashed=false`
      )
      
      const result = await this.request<{ files: Array<{ id: string }> }>(
        `${DRIVE_API}/files?q=${query}&spaces=drive`
      )

      const fileId = result.files[0]?.id
      if (!fileId) return null

      const content = await this.request<T>(
        `${DRIVE_API}/files/${fileId}?alt=media`
      )

      return content
    } catch (error) {
      console.error(`[ SYSTEM: FAILED TO READ ${fileName} ]`, error)
      return null
    }
  }

  async writeEntity<T>(fileName: string, data: T): Promise<void> {
    this.queueCount++
    this.updateQueueStatus()

    this.writeQueue = this.writeQueue.then(async () => {
      useSyncStore.getState().setSyncing(true)
      
      try {
        const folderId = useSyncStore.getState().folderId
        if (!folderId) throw new Error('Folder not initialised')

        const existingFileId = await this.findFile(fileName, folderId)
        
        if (existingFileId) {
          await this.updateFile(existingFileId, data)
        } else {
          await this.createFile(fileName, folderId, data)
        }

        useSyncStore.getState().setLastSync(new Date())
      } finally {
        this.queueCount--
        this.updateQueueStatus()
        useSyncStore.getState().setSyncing(this.queueCount > 0)
      }
    })

    await this.writeQueue
  }

  private async findFile(fileName: string, folderId: string): Promise<string | null> {
    const query = encodeURIComponent(
      `name='${fileName}' and '${folderId}' in parents and trashed=false`
    )
    
    const result = await this.request<{ files: Array<{ id: string }> }>(
      `${DRIVE_API}/files?q=${query}&spaces=drive`
    )

    return result.files[0]?.id ?? null
  }

  private async createFile<T>(
    fileName: string,
    folderId: string,
    data: T,
    isRetry = false
  ): Promise<string> {
    const metadata = {
      name: fileName,
      parents: [folderId],
      mimeType: 'application/json',
    }

    const form = new FormData()
    form.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' })
    )
    form.append(
      'file',
      new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    )

    const token = this.getAccessToken()
    const response = await fetch(
      `${UPLOAD_API}/files?uploadType=multipart`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      }
    )

    if (response.status === 401 && !isRetry) {
      console.log('[ SYSTEM: TOKEN EXPIRED. ATTEMPTING REFRESH... ]')
      const refreshed = await useAuthStore.getState().refreshToken()
      if (refreshed) {
        return this.createFile(fileName, folderId, data, true)
      }
      throw new Error('Session expired. Please sign in again.')
    }

    if (!response.ok) {
      throw new Error(`Failed to create file: ${response.status}`)
    }

    const result = await response.json()
    return result.id
  }

  private async updateFile<T>(fileId: string, data: T, isRetry = false): Promise<void> {
    const token = this.getAccessToken()
    const response = await fetch(
      `${UPLOAD_API}/files/${fileId}?uploadType=media`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data, null, 2),
      }
    )

    if (response.status === 401 && !isRetry) {
      console.log('[ SYSTEM: TOKEN EXPIRED. ATTEMPTING REFRESH... ]')
      const refreshed = await useAuthStore.getState().refreshToken()
      if (refreshed) {
        return this.updateFile(fileId, data, true)
      }
      throw new Error('Session expired. Please sign in again.')
    }

    if (!response.ok) {
      throw new Error(`Failed to update file: ${response.status}`)
    }
  }

  async listFiles(): Promise<Array<{ name: string; size: string; modifiedTime: string }>> {
    const folderId = useSyncStore.getState().folderId
    if (!folderId) return []

    const query = encodeURIComponent(`'${folderId}' in parents and trashed=false`)
    
    const result = await this.request<{
      files: Array<{ name: string; size: string; modifiedTime: string }>
    }>(
      `${DRIVE_API}/files?q=${query}&fields=files(name,size,modifiedTime)&spaces=drive`
    )

    return result.files
  }

  async deleteFile(fileName: string): Promise<boolean> {
    const folderId = useSyncStore.getState().folderId
    if (!folderId) return false

    const fileId = await this.findFile(fileName, folderId)
    if (!fileId) return false

    await this.request(`${DRIVE_API}/files/${fileId}`, { method: 'DELETE' })
    return true
  }

  async exportAllData(): Promise<Record<string, unknown>> {
    const files = await this.listFiles()
    const data: Record<string, unknown> = {}

    for (const file of files) {
      const content = await this.readEntity(file.name)
      if (content) {
        data[file.name] = content
      }
    }

    return data
  }

  // Settings-specific methods for nested folder structure

  private async findOrCreateSettingsFolder(): Promise<string> {
    const folderId = useSyncStore.getState().folderId
    if (!folderId) throw new Error('Main folder not initialised')

    // Check if settings folder exists
    const query = encodeURIComponent(
      `name='${SETTINGS_FOLDER}' and '${folderId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`
    )

    const result = await this.request<{ files: Array<{ id: string }> }>(
      `${DRIVE_API}/files?q=${query}&spaces=drive`
    )

    if (result.files[0]?.id) {
      return result.files[0].id
    }

    // Create settings folder
    const metadata = {
      name: SETTINGS_FOLDER,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [folderId],
    }

    const createResult = await this.request<{ id: string }>(
      `${DRIVE_API}/files`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metadata),
      }
    )

    return createResult.id
  }

  async readSettings<T>(fileName: string): Promise<T | null> {
    try {
      const settingsFolderId = await this.findOrCreateSettingsFolder()

      const query = encodeURIComponent(
        `name='${fileName}' and '${settingsFolderId}' in parents and trashed=false`
      )

      const result = await this.request<{ files: Array<{ id: string }> }>(
        `${DRIVE_API}/files?q=${query}&spaces=drive`
      )

      const fileId = result.files[0]?.id
      if (!fileId) return null

      const content = await this.request<T>(
        `${DRIVE_API}/files/${fileId}?alt=media`
      )

      return content
    } catch (error) {
      console.error(`[ SYSTEM: FAILED TO READ SETTINGS ${fileName} ]`, error)
      return null
    }
  }

  async writeSettings<T>(fileName: string, data: T): Promise<void> {
    const settingsFolderId = await this.findOrCreateSettingsFolder()
    const existingFileId = await this.findFile(fileName, settingsFolderId)

    if (existingFileId) {
      await this.updateFile(existingFileId, data)
    } else {
      await this.createFile(fileName, settingsFolderId, data)
    }
  }

  async deleteSettings(fileName: string): Promise<boolean> {
    try {
      const settingsFolderId = await this.findOrCreateSettingsFolder()
      const fileId = await this.findFile(fileName, settingsFolderId)
      if (!fileId) return false

      await this.request(`${DRIVE_API}/files/${fileId}`, { method: 'DELETE' })
      return true
    } catch (error) {
      console.error(`[ SYSTEM: FAILED TO DELETE SETTINGS ${fileName} ]`, error)
      return false
    }
  }
}

export const driveService = new GoogleDriveService()
